import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';
import { SustainabilityDashboardTabViewedProperties } from '@/app/core/analytics/mixpanel-events.types';
import { inject, Injectable } from '@angular/core';
import { SustainabilityDashboardService } from './sustainability-dashboard.service';

/** Route segment for each Sustainability Dashboard tab (matches SustainabilityTab enum values). */
export type SustainabilityDashboardTabRoute =
  | 'home_'
  | 'water'
  | 'soil'
  | 'climate'
  | 'biodiversity'
  | 'livelihood';

@Injectable({
  providedIn: 'root',
})
export class SustainabilityDashboardAnalyticsService {
  private readonly mixpanelTracker = inject(MixpanelTrackerService);
  private readonly dashboardService = inject(SustainabilityDashboardService);

  private lastEmittedSignature: string | null = null;

  /** Allows the same tab/context to emit again after an explicit refresh. */
  resetViewTracking(): void {
    this.lastEmittedSignature = null;
  }

  trackTabViewed(tab: SustainabilityDashboardTabRoute): void {
    try {
      const contractId = this.dashboardService.selectedContractId();
      if (!contractId) {
        return;
      }

      const subcontractId = this.dashboardService.selectedSubcontractId();
      const signature = `${tab}|${contractId}|${subcontractId ?? ''}`;
      if (signature === this.lastEmittedSignature) {
        return;
      }

      const properties = this.buildTabViewedProperties();
      this.dispatchTabViewedEvent(tab, properties);
      this.lastEmittedSignature = signature;
    } catch {
      // Analytics must never break dashboard UX
    }
  }

  private buildTabViewedProperties(): SustainabilityDashboardTabViewedProperties {
    const contractId = this.dashboardService.selectedContractId();
    const contractName = this.dashboardService.selectedContractName();
    const subcontractId = this.dashboardService.selectedSubcontractId();
    const subcontractName = this.dashboardService.selectedSubcontractName();

    return {
      'contract id': contractId ?? undefined,
      'contract name': contractName || undefined,
      'subcontract id': subcontractId ?? undefined,
      'subcontract name': subcontractName || undefined,
      'is dynamic': true,
    };
  }

  private dispatchTabViewedEvent(
    tab: SustainabilityDashboardTabRoute,
    properties: SustainabilityDashboardTabViewedProperties
  ): void {
    switch (tab) {
      case 'home_':
        this.mixpanelTracker.trackSustainabilityDashboardHomeViewed(properties);
        break;
      case 'water':
        this.mixpanelTracker.trackSustainabilityDashboardWaterViewed(properties);
        break;
      case 'soil':
        this.mixpanelTracker.trackSustainabilityDashboardSoilViewed(properties);
        break;
      case 'biodiversity':
        this.mixpanelTracker.trackSustainabilityDashboardBiodiversityViewed(properties);
        break;
      case 'climate':
        this.mixpanelTracker.trackSustainabilityDashboardClimateViewed(properties);
        break;
      case 'livelihood':
        this.mixpanelTracker.trackSustainabilityDashboardLivelihoodViewed(properties);
        break;
    }
  }
}
